import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID, signal, WritableSignal } from '@angular/core';
import { IBlogPost } from '../../models';
import { RequestService } from './request.service';

@Injectable({
  providedIn: 'root',
})
export class BlogService {
  isDone = signal(false);
  blogPosts: WritableSignal<IBlogPost[]> = signal([]);

  constructor(
    private request: RequestService,
    private http: HttpClient,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  async getBlogPosts(): Promise<IBlogPost[]> {
    if (this.isDone()) {
      return this.blogPosts();
    }

    let posts = [];
    let localeStr = this.locale.substring(0, 2);
    try {
      let blogArticles: any;
      switch (localeStr) {
        case 'da':
          blogArticles = (await import(`../../../assets/i18n/blog-posts.da.json`)).default;
          break;
        case 'nl':
          blogArticles = (await import(`../../../assets/i18n/blog-posts.nl.json`)).default;
          break;
        case 'de':
        default:
          blogArticles = (await import(`../../../assets/i18n/blog-posts.de.json`)).default;
          break;
      }

      posts = Object.keys(blogArticles)
        .map((key) => {
          return {
            ...blogArticles[key],
            uri: key
          };
        })
        // Neueste zuerst. Das CMS liefert kein Veroeffentlichungsdatum (`sort` ist ueberall
        // null), die aufsteigende id entspricht aber der Anlagereihenfolge im CMS.
        .sort((a, b) => b.id - a.id);
    } catch (e) {
      console.warn('Could not load translations', e);
    }

    this.blogPosts.set(posts);
    this.isDone.set(true);
    return posts;
  }

  getPostByUri(uri: string): IBlogPost {
    return this.blogPosts().find((post) => post.uri === uri);
  }

  generateBlogPostUri(post: IBlogPost): string {
    // Konvertiere den Titel in eine SEO-freundliche URL
    // 1. Zu Kleinbuchstaben konvertieren
    let slug = post.title.toLowerCase();

    // 2. Deutsche Umlaute und ß ersetzen
    slug = slug
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss');

    // 3. Alle Sonderzeichen durch Bindestriche ersetzen und mehrere Bindestriche zusammenfassen
    slug = slug
      .replace(/[^\w\s-]/g, '') // Entferne alle nicht-alphanumerischen Zeichen außer Leerzeichen und Bindestriche
      .replace(/\s+/g, '-')     // Ersetze Leerzeichen durch Bindestriche
      .replace(/-+/g, '-');     // Mehrere aufeinanderfolgende Bindestriche durch einen ersetzen

    // 4. Führende und nachfolgende Bindestriche entfernen
    slug = slug.replace(/^-+/, '').replace(/-+$/, '');

    return `/blog/${slug}`;
  }
}
